import React from 'react';
import Icon from '../AppIcon';

/**
 * Reusable pagination component.
 * Props:
 *   currentPage  {number}   1-indexed current page
 *   totalPages   {number}   total number of pages
 *   totalItems   {number}   total record count
 *   pageSize     {number}   records per page
 *   onPageChange {function} called with new page number
 */
const Pagination = ({ currentPage = 1, totalPages = 1, totalItems = 0, pageSize = 10, onPageChange }) => {
  if (totalPages <= 1) return null;

  const start = (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalItems);

  // Build page number array with ellipsis
  const getPages = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages?.push(i);
    } else {
      pages?.push(1);
      if (currentPage > 3) pages?.push('...');
      const rangeStart = Math.max(2, currentPage - 1);
      const rangeEnd = Math.min(totalPages - 1, currentPage + 1);
      for (let i = rangeStart; i <= rangeEnd; i++) pages?.push(i);
      if (currentPage < totalPages - 2) pages?.push('...');
      pages?.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="flex items-center justify-between px-4 py-12 border-t border-slate-100 mt-4">
      <div className="text-xs text-slate-500">
        Showing <span className="font-semibold text-slate-700">{start}–{end}</span> of <span className="font-semibold text-slate-700">{totalItems}</span>
      </div>
      <div className="flex items-center gap-4">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="p-8 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <Icon name="ChevronLeft" size={14} />
        </button>

        {getPages()?.map((page, idx) =>
          page === '...' ? (
            <span key={`ellipsis-${idx}`} className="px-8 py-6 text-xs text-slate-400">…</span>
          ) : (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={`min-w-[32px] h-8 px-8 rounded-lg text-xs font-semibold transition-all ${
                page === currentPage
                  ? 'bg-gradient-to-r from-primary to-secondary text-white shadow-sm'
                  : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {page}
            </button>
          )
        )}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="p-8 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <Icon name="ChevronRight" size={14} />
        </button>
      </div>
    </div>
  );
};

export default Pagination;
